import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'
import {
  LoadingOverlay,
  NoRowsOverlay,
  Pagination,
  Toolbar,
} from '@components/tables/components'
import {
  Box,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material'
import {
  DataGridPro,
  GridColumns,
  GridSelectionModel,
} from '@mui/x-data-grid-pro'
import { RequestingOrganisationDto } from '@services/RequestingOrganisationsService'
import { Actions } from '@state/actions'
import { useSelector } from '@state/store'
import {
  findAllOrganisations,
  removeOrganisation,
} from '@state/thunks/organisationThunk'
import { findAll as findAllSites } from '@state/thunks/siteThunk'
import { debounce } from 'lodash'
import { EditIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { OrganisationForm } from './OrganisationForm'

const PAGE_SIZE_OPTIONS = [10, 25, 50]

export default function Organisations() {
  const intl = useIntl()
  const dispatch = useDispatch()

  const [openDialog, setOpenDialog] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [editingOrganisation, setEditingOrganisation] = useState<
    RequestingOrganisationDto | undefined
  >(undefined)
  const [selectedSiteId, setSelectedSiteId] = useState<number>(-1)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0])

  const { loading, organisations, totalCount, sites } = useSelector(
    ({ organisation, loading, site }) => ({
      organisations: organisation.organisations,
      totalCount: organisation.totalCount,
      sites: site.sites.datas ?? [],
      loading:
        loading[Actions.ORGANISATION_FIND_ALL] ||
        loading[Actions.ORGANISATION_CREATE] ||
        loading[Actions.ORGANISATION_UPDATE] ||
        loading[Actions.ORGANISATION_REMOVE],
    }),
  )

  const columns: GridColumns<RequestingOrganisationDto> = useMemo(
    () => [
      { field: 'name', headerName: 'Nom', flex: 1 },
      { field: 'city', headerName: 'Ville', flex: 1 },
      { field: 'zipCode', headerName: 'Code postal', flex: 1 },
      { field: 'address', headerName: 'Adresse', flex: 1 },
      { field: 'siret', headerName: 'SIRET', flex: 1 },
      { field: 'billingEmail', headerName: 'Email facturation', flex: 2 },
      {
        field: 'chorusEnabled',
        headerName: 'Chorus',
        flex: 1,
        valueFormatter: (params) => (params.value ? 'Oui' : 'Non'),
      },
      {
        field: 'chorusServiceCode',
        headerName: 'Code service Chorus',
        flex: 1,
        valueFormatter: (params) => params.value ?? '',
      },
      {
        field: 'site',
        headerName: 'Site',
        flex: 1,
        valueFormatter: (params) => params.value?.label ?? '',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        align: 'right',
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={() => {
              setEditingOrganisation(params.row)
              setOpenDialog(true)
            }}
          >
            <EditIcon />
          </IconButton>
        ),
      },
    ],
    [],
  )

  const debouncedSearch = useMemo(() => {
    return debounce((searchValue: string) => {
      setPage(0)
      dispatch(
        findAllOrganisations({
          ...(selectedSiteId !== -1 ? { siteId: selectedSiteId } : {}),
          ...(searchValue ? { search: searchValue } : {}),
          page: 0,
          limit: pageSize,
        }),
      )
    }, 400)
  }, [dispatch, selectedSiteId, pageSize])

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const fetchOrganisations = useCallback(() => {
    dispatch(
      findAllOrganisations({
        ...(selectedSiteId !== -1 ? { siteId: selectedSiteId } : {}),
        ...(search ? { search } : {}),
        page,
        limit: pageSize,
      }),
    )
  }, [dispatch, selectedSiteId, search, page, pageSize])

  useEffect(() => {
    fetchOrganisations()
  }, [])

  useEffect(() => {
    dispatch(findAllSites())
  }, [dispatch])

  const _onDeleteConfirm = () => {
    dispatch(removeOrganisation({ ids: selectedRows }))
    setIsDeleteDialogOpen(false)
    setSelectedRows([])
  }

  const _onSelectionChange = (selection: GridSelectionModel) => {
    setSelectedRows(selection as string[])
  }

  const _onCloseDialog = () => {
    setOpenDialog(false)
    setEditingOrganisation(undefined)
  }

  const _onPageChange = (newPage: number) => {
    setPage(newPage)
  }

  const _onPageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(0)
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'end',
        height: '95%',
        padding: '8px',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, my: 1, width: '100%' }}>
        <TextField
          select
          size="small"
          label="Site"
          value={selectedSiteId}
          onChange={(e) => {
            setSelectedSiteId(Number(e.target.value))
            setPage(0)
          }}
          sx={{ width: 300, backgroundColor: 'white' }}
        >
          <MenuItem value={-1}>Tous les sites</MenuItem>
          {sites.map((site) => (
            <MenuItem key={site.id} value={site.id}>
              {site.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Rechercher par nom..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            debouncedSearch(e.target.value)
          }}
          sx={{ width: 400, backgroundColor: 'white' }}
        />
      </Box>

      <Paper
        elevation={2}
        style={{ flex: 1, width: '100%', borderRadius: '0px 15px 15px 15px' }}
      >
        <DataGridPro
          rows={organisations}
          columns={columns}
          loading={loading}
          rowCount={totalCount}
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onPageChange={_onPageChange}
          onPageSizeChange={_onPageSizeChange}
          checkboxSelection
          disableSelectionOnClick
          onSelectionModelChange={_onSelectionChange}
          disableColumnMenu
          components={{
            Toolbar,
            Pagination,
            NoRowsOverlay,
            LoadingOverlay,
          }}
          componentsProps={{
            toolbar: {
              title: 'organisations',
              onAdd: () => {
                setEditingOrganisation(undefined)
                setOpenDialog(true)
              },
              onRefresh: fetchOrganisations,
              onDelete: () => {
                selectedRows.length
                  ? setIsDeleteDialogOpen(true)
                  : alert('Veuillez sélectionner au moins une organisation')
              },
            },
          }}
          sx={{
            padding: '6px',
          }}
        />
      </Paper>

      <Dialog
        open={openDialog}
        onClose={_onCloseDialog}
        maxWidth="lg"
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          {editingOrganisation
            ? intl.formatMessage({ id: 'editOrganisation' })
            : intl.formatMessage({ id: 'addOrganisation' })}
        </DialogTitle>
        <DialogContent>
          <OrganisationForm
            onClose={_onCloseDialog}
            row={editingOrganisation}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={_onDeleteConfirm}
        title="Confirmer la suppression"
        description="Êtes-vous sûr de vouloir supprimer les organisations sélectionnées ?"
      />
    </Container>
  )
}
